import type { ImageType } from '@vizit/api';
import { Button, ButtonLevel, ButtonSize, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { PlusIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import type * as React from 'react';
import { ImageTypeChip } from '../image-type-chip';
import { ScoringMeasureGroup, ScoringMeasureInfo, ScoringMeasureLink } from '../scoring-measure-group';
import { scoredAgainstLabel, showingLabel } from '../scoring-measure-group';

/** What each control of the band does to the scores, in the info tooltip after it. */
const BENCHMARK_INFO =
  'What your assets are scored against: the top images on Amazon or Walmart, or a benchmark of your own.';
const PRODUCT_CATEGORY_INFO = 'The category sets the shopper audience used to score your assets.';

export interface CollectionScoringBarProps {
  /**
   * How many assets the grid shows, read as "Showing N assets", then "… scored against" once `isScored`
   */
  assetCount: number;
  /**
   * Whether the grid shows scores under the selected pair, i.e. its scoring is ready. Only then does the bar read
   * "Showing N assets scored against"
   *
   * @optional
   * @default false
   */
  isScored?: boolean;
  /**
   * The benchmark control, e.g. the benchmark picker
   */
  benchmarkControl: React.ReactNode;
  /**
   * The product category control, e.g. the category picker. Leave unset while the collection has no category under
   * the benchmark: the bar then offers "Add product category" instead
   *
   * @optional
   */
  productCategoryControl?: React.ReactNode;
  /**
   * Called from "Add product category"
   *
   * @optional
   */
  onAddProductCategory?: () => void;
  /**
   * Whether "Add product category" is disabled, e.g. while no benchmark is chosen
   *
   * @optional
   * @default false
   */
  isAddProductCategoryDisabled?: boolean;
  /**
   * The collection's image type, shown locked after the pickers; leave unset while unknown
   *
   * @optional
   */
  imageType?: ImageType;
  /**
   * Inline status after the controls, e.g. the scoring progress
   *
   * @optional
   */
  status?: React.ReactNode;
  /**
   * Controls on the right, e.g. "Upload assets"
   *
   * @optional
   */
  actions?: React.ReactNode;
  /**
   * Additional CSS classes for the root element
   *
   * @optional
   */
  className?: string;
}

/**
 * The bar over a collection's grid that says what the scores are measured against: "Showing N assets", with "scored
 * against" once the selected pair's scores show, then in one grey group the benchmark control, the product category control (or "Add product category"
 * while there is none), each followed by an info tooltip saying what it does to the scores, and the collection's
 * locked image type, linked by a glyph, then an optional inline status. `actions` fills the right.
 */
export function CollectionScoringBar(props: CollectionScoringBarProps) {
  const {
    assetCount,
    isScored = false,
    benchmarkControl,
    productCategoryControl,
    onAddProductCategory,
    isAddProductCategoryDisabled = false,
    imageType,
    status,
    actions,
    className,
  } = props;

  return (
    <div className={cx('flex flex-wrap items-center justify-between gap-6', className)}>
      {/* Takes the room the actions leave, never less than its controls, so a long status wraps inside it and the
          actions keep their place on the right */}
      <div className="flex min-w-min flex-1 flex-wrap items-center gap-6">
        <Text size={TextSize.Sm} weight={TextWeight.Medium} color={TextColor.Quaternary}>
          {isScored ? scoredAgainstLabel(assetCount, 'asset') : showingLabel(assetCount, 'asset')}
        </Text>
        <ScoringMeasureGroup className="gap-8">
          {benchmarkControl && (
            <>
              <span className="flex items-center gap-4">
                {benchmarkControl}
                <ScoringMeasureInfo aria-label="About the benchmark">{BENCHMARK_INFO}</ScoringMeasureInfo>
              </span>
              <ScoringMeasureLink />
            </>
          )}
          <span className="flex items-center gap-4">
            {productCategoryControl ?? (
              <Button
                level={ButtonLevel.Secondary}
                size={ButtonSize.Sm}
                iconStart={<PlusIcon size={14} aria-hidden />}
                onPress={onAddProductCategory}
                isDisabled={isAddProductCategoryDisabled}
              >
                Add product category
              </Button>
            )}
            <ScoringMeasureInfo aria-label="About the product category">{PRODUCT_CATEGORY_INFO}</ScoringMeasureInfo>
          </span>
          {imageType && (
            <>
              <ScoringMeasureLink />
              <ImageTypeChip scope={imageType} />
            </>
          )}
        </ScoringMeasureGroup>
        {status}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-4">{actions}</div>}
    </div>
  );
}
